import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, BarChart3, FlaskConical, FileDown, FileSpreadsheet, History as HistoryIcon, Calendar as CalendarIcon, Download } from "lucide-react";
import Layout from "@/components/Layout";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const RANGE_OPTIONS = [
  { id: "24h",    label: "24 horas" },
  { id: "7d",     label: "7 dias" },
  { id: "30d",    label: "30 dias" },
  { id: "all",    label: "Tudo" },
  { id: "custom", label: "Personalizar" },
];

const KIND_OPTIONS = [
  { id: "all", label: "Todos", color: "bg-slate-100 text-slate-700" },
  { id: "anova", label: "Testes com ANOVA", color: "bg-[#FFF1E6] text-[#E65C00]" },
  { id: "sensory", label: "Escolha Forçada", color: "bg-[#EEF7E4] text-[#558B2F]" },
];

export default function Historico() {
  const [range, setRange] = useState("7d");
  const [kind, setKind] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const isCustomReady = range !== "custom" || (dateFrom && dateTo);

  const load = async () => {
    if (!isCustomReady) return;
    setLoading(true);
    try {
      const params = { range, kind };
      if (range === "custom") { params.from = dateFrom; params.to = dateTo; }
      const { data } = await api.get("/history/me", { params });
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [range, kind, dateFrom, dateTo]);

  const exportCsv = async () => {
    if (!isCustomReady) {
      toast.error("Selecione as datas antes de exportar");
      return;
    }
    setExporting(true);
    try {
      const params = new URLSearchParams({ range, kind });
      if (range === "custom") { params.append("from", dateFrom); params.append("to", dateTo); }
      const token = localStorage.getItem("sensorize_token");
      const res = await fetch(`${API_BASE}/history/me/export.csv?${params.toString()}`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Falha ao exportar CSV");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `historico_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      toast.success("CSV baixado");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setExporting(false);
    }
  };

  const downloadFile = (item, ext) => {
    const path = item.type === "anova" ? "anova" : "sensory";
    const url = `${API_BASE}/${path}/${item.id}/export/${ext}`;
    const token = localStorage.getItem("sensorize_token");
    fetch(url, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => { if (!r.ok) throw new Error("Falha ao exportar"); return r.blob(); })
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${path}_${item.id}.${ext === "pdf" ? "pdf" : "xlsx"}`;
        a.click();
      })
      .catch((e) => toast.error(e.message));
  };

  const counts = data?.counts;
  const items = data?.items || [];

  return (
    <Layout>
      <div className="mb-8">
        <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Meu histórico</p>
        <h1 className="text-3xl sm:text-4xl font-heading font-extrabold text-slate-900 mt-1">
          Análises realizadas
        </h1>
        <p className="text-slate-600 mt-2 max-w-2xl">
          Consulte as análises que você executou. Filtre por período (horas/dias/semanas) e por tipo.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <SummaryCard
          label="Total no período"
          value={counts?.total ?? "—"}
          icon={<HistoryIcon className="w-6 h-6" />}
          tone="slate"
        />
        <SummaryCard
          label="Testes com ANOVA"
          value={counts?.anova ?? "—"}
          icon={<BarChart3 className="w-6 h-6" />}
          tone="orange"
        />
        <SummaryCard
          label="Escolha Forçada"
          value={counts?.sensory ?? "—"}
          icon={<FlaskConical className="w-6 h-6" />}
          tone="green"
        />
      </div>

      <Card className="border-slate-200 mb-6">
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-wrap gap-6 items-center">
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold mr-1">Período</span>
              {RANGE_OPTIONS.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setRange(r.id)}
                  data-testid={`range-${r.id}`}
                  className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                    range === r.id ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold mr-1">Tipo</span>
              {KIND_OPTIONS.map((k) => (
                <button
                  key={k.id}
                  type="button"
                  onClick={() => setKind(k.id)}
                  data-testid={`kind-${k.id}`}
                  className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                    kind === k.id ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                >
                  {k.label}
                </button>
              ))}
            </div>
            <div className="ml-auto">
              <Button
                type="button"
                onClick={exportCsv}
                disabled={exporting || loading || !isCustomReady}
                data-testid="export-csv-button"
                className="bg-[#7CB342] hover:bg-[#558B2F] text-white font-semibold"
              >
                {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Exportar CSV
              </Button>
            </div>
          </div>

          {range === "custom" && (
            <div className="flex flex-wrap items-end gap-3 pt-2 border-t border-slate-100" data-testid="custom-date-range">
              <div className="flex items-center gap-2 text-slate-500">
                <CalendarIcon className="w-4 h-4" />
                <span className="text-xs uppercase tracking-widest font-semibold">Intervalo personalizado</span>
              </div>
              <div>
                <Label htmlFor="date-from" className="text-xs text-slate-600">De</Label>
                <Input
                  id="date-from" type="date" value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  data-testid="date-from-input" className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="date-to" className="text-xs text-slate-600">Até</Label>
                <Input
                  id="date-to" type="date" value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  data-testid="date-to-input" className="mt-1"
                />
              </div>
              {(!dateFrom || !dateTo) && (
                <span className="text-xs text-amber-600 pb-2">Selecione as duas datas para visualizar.</span>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 flex items-center justify-center text-slate-500">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Carregando…
            </div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-slate-500">
              Nenhuma análise encontrada para os filtros selecionados.
              <div className="mt-3 flex justify-center gap-3">
                <Link to="/anova"><Button variant="outline" size="sm">Ir para ANOVA</Button></Link>
                <Link to="/sensorial"><Button variant="outline" size="sm">Ir para Escolha Forçada</Button></Link>
              </div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Módulo</TableHead>
                  <TableHead>Detalhe</TableHead>
                  <TableHead>Resultado</TableHead>
                  <TableHead className="text-right">Exportar</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="history-table-body">
                {items.map((it) => (
                  <TableRow key={it.id} data-testid={`history-row-${it.id}`}>
                    <TableCell className="text-sm text-slate-600 whitespace-nowrap">
                      {new Date(it.created_at).toLocaleString("pt-BR")}
                    </TableCell>
                    <TableCell>
                      <Badge className={it.type === "anova" ? "bg-[#FFF1E6] text-[#E65C00]" : "bg-[#EEF7E4] text-[#558B2F]"}>
                        {it.type === "anova" ? "ANOVA" : "Escolha Forçada"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {it.type === "anova" ? (
                        <>
                          {it.title && (
                            <div className="text-sm font-bold text-slate-900" data-testid="row-title">{it.title}</div>
                          )}
                          <div className={it.title ? "text-slate-700" : "font-semibold text-slate-900"}>{it.test_name}</div>
                          <div className="text-xs text-slate-500">
                            {it.post_hoc} · confiança {(it.confidence ?? 0.95) * 100}%
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="font-semibold text-slate-900">{it.test_label}</div>
                          <div className="text-xs text-slate-500">
                            {it.mode === "difference" ? "Diferença" : "Similaridade"} · {it.panel === "trained" ? "Treinado" : it.panel === "selected" ? "Selecionado" : "Consumidor"} · {(it.confidence ?? 0.95) * 100}%
                          </div>
                        </>
                      )}
                    </TableCell>
                    <TableCell>
                      <SummaryBadge item={it} />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-1">
                        <Button variant="outline" size="sm" onClick={() => downloadFile(it, "pdf")} data-testid={`export-pdf-${it.id}`}>
                          <FileDown className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => downloadFile(it, "excel")} data-testid={`export-xlsx-${it.id}`}>
                          <FileSpreadsheet className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Layout>
  );
}

function SummaryBadge({ item }) {
  const s = item.summary || {};
  if (item.type === "anova") {
    return (
      <Badge className={s.significant ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}>
        {s.significant ? "Significativo" : "Não significativo"}
      </Badge>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <Badge className={s.significant ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}>
        {s.decision || "—"}
      </Badge>
      {s.n_correct != null && s.n_total != null && (
        <span className="text-xs text-slate-500">{s.n_correct}/{s.n_total}</span>
      )}
    </div>
  );
}

function SummaryCard({ label, value, icon, tone }) {
  const map = {
    slate: { bg: "bg-slate-100", fg: "text-slate-700" },
    orange: { bg: "bg-[#FFF1E6]", fg: "text-[#FF8A50]" },
    green:  { bg: "bg-[#EEF7E4]", fg: "text-[#7CB342]" },
  };
  const t = map[tone] || map.slate;
  return (
    <Card className="border-slate-200">
      <CardContent className="p-5 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold">{label}</div>
          <div className="text-3xl font-heading font-extrabold text-slate-900 mt-1">{value}</div>
        </div>
        <div className={`w-12 h-12 rounded-xl ${t.bg} ${t.fg} flex items-center justify-center`}>
          {icon}
        </div>
      </CardContent>
    </Card>
  );
}
